import React, { useState, useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import FaultCustomerDefinitionDialog from './components/FaultCustomerDefinitionDialog';
import useFaultCustomerDefinitions from './hooks/useFaultCustomerDefinitions';
import FaultCustomerDefinitionFilter from './components/FaultCustomerDefinitionFilter';
import { useGetFaultClientDefinitionByIdQuery } from '../../../../api';
import { dateOnlyBodyTemplate } from '../../../../utils/dateUtils';
import { useGetAllLanguagesQuery } from '../../../../api/endpoints/language';
import { useLanguage } from '../../../../contexts/LanguageContext';
const LANGUAGE_TEXT_TO_CODE_MAP = {
        türkçe: 'tr',
        english: 'en',
        german: 'de',
        french: 'fr',
        italian: 'it',
        romanian: 'ro'
        
    };
const FaultCustomerDefinitionsPage = () => {
    const { t } = useLanguage();
    const [dialogVisible, setDialogVisible] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);

    const { data: selectedItemData } = useGetFaultClientDefinitionByIdQuery(
        {
            id: selectedId,
            status: selectedStatus
        },
        {
            skip: !selectedId
        }
    );

    const {
        data,
        loading,
        filters,
        lazyParams,
        onPage,
        onSort,
        filterFields,
        customFilterTypes,
        customFilterOptions,
        handleSearch,
        handleApplyFilters,
        handleResetFilters,
        handleCreate: createFaultDefinition,
        handleUpdate: updateFaultDefinition,
        handleDelete: deleteFaultDefinition
    } = useFaultCustomerDefinitions();

    const columnsRef = useRef(null);
    const handleCreate = () => {
        setSelectedItem(null);
        setSelectedId(null);
        setSelectedStatus(null);
        setIsEdit(false);
        setDialogVisible(true);
    };

    const handleEdit = (rowData) => {
         setSelectedItem(rowData);
        setIsEdit(true);
        setDialogVisible(true);

        setTimeout(() => {
            setSelectedId(rowData.id); 
            setSelectedStatus(rowData.status);
            setIsEdit(true); 
        }, 0);
    };

    React.useEffect(() => {
        if (isEdit && selectedItemData) {
            setSelectedItem(selectedItemData);
            setDialogVisible(true);
        }
    }, [selectedItemData, isEdit]);

    const { data: fetchedLanguagesFromApi, isLoading: isLoadingLanguages } = useGetAllLanguagesQuery();

    const supportedLanguagesForDialog = useMemo(() => {
        const languages = fetchedLanguagesFromApi || [];

        if (isLoadingLanguages || !Array.isArray(languages) || languages.length === 0) return [];

        return languages
            .map((langFromApi) => {
                if (!langFromApi || langFromApi.id === undefined || langFromApi.id === null || !langFromApi.text) {
                    return null;
                }

                const apiLangText = String(langFromApi.text).toLowerCase().trim();
                const langCode = LANGUAGE_TEXT_TO_CODE_MAP[apiLangText];
                console.log('apiLangText', apiLangText, '-> code:', langCode);

                if (!langCode) {
                    return null;
                }
                return {
                    id: langFromApi.id,
                    code: langCode,
                    name: langFromApi.text
                };
            })
            .filter(Boolean);
    }, [fetchedLanguagesFromApi, isLoadingLanguages]);

    const handleDelete = (rowData) => {
        confirmDialog({
            message: t('warranty.faultCustomerDefinitions.confirmDialog.deleteMessage'),
            header: t('warranty.faultCustomerDefinitions.confirmDialog.deleteHeader'),

            icon: 'pi pi-exclamation-triangle',
            acceptLabel: t('common.yes'),
            rejectLabel: t('common.no'), 
            accept: () => deleteFaultDefinition(rowData.id),
            reject: () => {},
            rejectClassName: 'p-button-outlined'
        });
    };

    const handleDialogClose = () => {
        setDialogVisible(false);
        setSelectedItem(null);
        setSelectedId(null);
        setSelectedStatus(null);
        setIsEdit(false);
    };

    const handleSubmit = async (formData) => {
        const success = isEdit ? await updateFaultDefinition(formData) : await createFaultDefinition(formData);
        if (success) {
            setDialogVisible(false);
        }
    };

    const statusBodyTemplate = (rowData) => {
        return <span className={`p-badge p-badge-${rowData.isActive ? 'success' : 'danger'}`}>{rowData.isActive ? t('warranty.faultCustomerDefinitions.dialog.status0') : t('warranty.faultCustomerDefinitions.dialog.status1')}</span>;
    };

    const faultTypeBodyTemplate = (rowData) => {
        const faultType = rowData.faultType;
        return faultType === 1 ? t('warranty.faultCustomerDefinitions.dialog.faultType1') : t('warranty.faultCustomerDefinitions.dialog.faultType2');
    };

    const trTextBodyTemplate = (rowData) => {
        return rowData.translations?.find((t) => t.languageId == 2)?.text || '';
    };

    const enTextBodyTemplate = (rowData) => {
        return rowData.translations?.find((t) => t.languageId == 1)?.text || '';
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2 justify-content-center">
                <i className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={t('customer.editTooltip')} onClick={() => handleEdit(rowData)} />
                <i className="pi pi-trash cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={t('customer.deleteTooltip')} onClick={() => handleDelete(rowData)} />
            </div>
        );
    };

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{t('warranty.faultCustomerDefinitions.title')}</h1>
                <Button label={t('warranty.faultCustomerDefinitions.newRecord')} icon="pi pi-plus" onClick={handleCreate} />
            </div>

            <FaultCustomerDefinitionFilter
                filterFields={filterFields}
                columns={columnsRef.current}
                customFilterTypes={customFilterTypes}
                customFilterOptions={customFilterOptions}
                handleSearch={handleSearch}
                supportedLanguages={supportedLanguagesForDialog}
                handleApplyFilters={handleApplyFilters}
                handleResetFilters={handleResetFilters}
            />
            <DataTable
                value={data?.items || []}
                lazy
                paginator
                first={lazyParams.first}
                rows={lazyParams.rows}
                totalRecords={data?.totalCount || 0}
                onPage={onPage}
                onSort={onSort}
                sortField={lazyParams.sortField}
                sortOrder={lazyParams.sortOrder}
                loading={loading}
                filters={filters}
                filterDisplay="menu"
                globalFilterFields={['productGroup', 'faultType', 'startDate', 'endDate', 'status']}
                emptyMessage={t('warranty.faultCustomerDefinitions.table.emptyMessage')}
                className="p-datatable-gridlines"
                rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]}
                sortMode="single"
                removableSort
                showGridlines
                stripedRows
            >
                <Column body={actionBodyTemplate} style={{ width: '5%' }} align="center" />
                <Column sortable field="faultType" header={t('warranty.faultCustomerDefinitions.table.faultType')} body={faultTypeBodyTemplate} />
                <Column sortable field="productGroup" header={t('warranty.faultCustomerDefinitions.table.productGroup')} />
                <Column sortable field="trText" header={t('warranty.faultCustomerDefinitions.table.trText')} body={trTextBodyTemplate} />
                <Column sortable field="enText" header={t('warranty.faultCustomerDefinitions.table.enText')} body={enTextBodyTemplate} />
                <Column sortable field="startDate" header={t('warranty.faultCustomerDefinitions.table.startDate')} body={(rowData) => dateOnlyBodyTemplate(rowData, 'startDate')} />
                <Column sortable field="endDate" header={t('warranty.faultCustomerDefinitions.table.endDate')} body={(rowData) => dateOnlyBodyTemplate(rowData, 'endDate')} />
                <Column field="isActive" body={statusBodyTemplate} style={{ width: '10%', minWidth: '100px' }} />
            </DataTable>

            <FaultCustomerDefinitionDialog visible={dialogVisible} onHide={handleDialogClose} onSubmit={handleSubmit} initialData={selectedItem} isEdit={isEdit} loading={loading} supportedLanguages={supportedLanguagesForDialog} />

            <ConfirmDialog />
        </div>
    );
};

export default FaultCustomerDefinitionsPage;
